import React, { useMemo } from 'react';
import * as d3 from 'd3';
import type { RouteWaypoint, HourlyWeatherSample, UnitsMode } from '../types';
import { celsiusToFahrenheit, formatWindSpeedOnly } from '../utils/units';
import { CarNavIcon } from './icons/OnTheRouteIcons';

type DriveChartProps = {
  waypoints: RouteWaypoint[];
  waypointWeather: Map<string, HourlyWeatherSample>;
  routeDurationSec: number;
  routeDistanceM: number;
  departureTime: Date;
  scrubProgress: number; // 0 to 1
  onScrubChange?: (progress: number) => void;
  unitsMode: UnitsMode;
  dark?: boolean;
  width?: number;
};

export const DriveChart: React.FC<DriveChartProps> = ({
  waypoints,
  waypointWeather,
  routeDurationSec,
  routeDistanceM,
  departureTime,
  scrubProgress,
  onScrubChange,
  unitsMode,
  dark = false,
  width = 330,
}) => {
  const isImperial = unitsMode === 'imperial';
  const totalDist = isImperial ? routeDistanceM / 1609.344 : routeDistanceM / 1000;
  const distUnit = isImperial ? 'mi' : 'km';

  // Sample data points along the route
  const points = useMemo(() => {
    return waypoints.map((wp) => {
      const dist = isImperial ? wp.distanceM / 1609.344 : wp.distanceM / 1000;
      const weather = waypointWeather.get(wp.id);
      const tempC = weather?.temperatureC ?? 20;
      const tempF = celsiusToFahrenheit(tempC);
      const displayTemp = isImperial ? tempF : tempC;

      const pop = weather?.precipProbability ?? 0;
      const windKmh = weather?.windKmh ?? 15;
      const windDisplay = formatWindSpeedOnly(windKmh, unitsMode);

      const etaMs = departureTime.getTime() + wp.travelTimeSec * 1000;
      const etaDate = new Date(etaMs);
      const etaClock = etaDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      const etaClockCompact = etaClock.replace(/[\s\u202f]*(AM|PM)$/i, (_, ap) =>
        String(ap).toUpperCase() === 'AM' ? 'a' : 'p',
      );

      let name = wp.placeName ? wp.placeName.split(',')[0]?.trim() || '' : '';
      if (wp.routeProgress === 0) {
        name = name || 'Start';
      } else if (wp.routeProgress >= 0.98) {
        name = name || 'Dest';
      }

      return {
        id: wp.id,
        dist,
        progress: wp.routeProgress,
        displayTemp,
        pop,
        windDisplay,
        etaClock,
        etaClockCompact,
        name,
      };
    });
  }, [waypoints, waypointWeather, isImperial, departureTime, unitsMode]);

  // If insufficient data, return placeholder
  if (points.length < 2 || totalDist <= 0) {
    return (
      <div style={{ height: 168, display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="c2 t-sub">
        Loading route profile...
      </div>
    );
  }

  // Dimensions
  const CW = Math.max(260, width);
  const X0 = 16;
  const X1 = CW - 16;
  const TOP = 24;
  const BASE = 78;
  const AXIS = 98;
  const HEIGHT = 168;
  const isNarrow = CW < 300;
  const labelFontSize = 11;
  const LABEL_NAME_Y = AXIS + 16;
  const LABEL_TIME_Y = AXIS + 32;

  const xScale = (d: number) => X0 + (d / (totalDist || 1)) * (X1 - X0);

  // Temperature domain: min and max with padding
  const temps = points.map((p) => p.displayTemp);
  const minTemp = Math.min(...temps, isImperial ? 50 : 10);
  const maxTemp = Math.max(...temps, isImperial ? 90 : 32);
  const yTemp = (t: number) => BASE - ((t - minTemp) / (maxTemp - minTemp || 1)) * (BASE - TOP);

  // Temperature color scale
  const tcolF = d3
    .scaleLinear<string>()
    .domain([56, 63, 70, 77, 84])
    .range(['#33B6E8', '#5DD3B6', '#FFCC00', '#FF9F0A', '#FF6A3D'])
    .interpolate(d3.interpolateRgb);

  const tcol = (t: number) => {
    const degF = isImperial ? t : t * 1.8 + 32;
    return tcolF(degF);
  };

  // Interpolated points for smooth curve
  const curvePoints: [number, number][] = points.map((p) => [xScale(p.dist), yTemp(p.displayTemp)]);
  const lineGenerator = d3.line().curve(d3.curveMonotoneX);
  const areaGenerator = d3.area().curve(d3.curveMonotoneX).y0(AXIS);

  const pathD = lineGenerator(curvePoints) || '';
  const areaD = areaGenerator(curvePoints) || '';

  // Active scrub marker
  const scrubDist = scrubProgress * totalDist;
  const scrubX = xScale(scrubDist);
  const scrubEtaMs = departureTime.getTime() + scrubProgress * routeDurationSec * 1000;
  const scrubEta = new Date(scrubEtaMs).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Handle pointer down on chart to scrub
  const handlePointer = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!onScrubChange) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, (clickX - X0) / (X1 - X0)));
    onScrubChange(progress);
  };

  // Select evenly spaced guide points for labels (3 on narrow charts)
  const guideCount = isNarrow ? 3 : 4;
  const guideIndices = useMemo(() => {
    if (points.length <= guideCount) return points.map((_, i) => i);
    const step = (points.length - 1) / (guideCount - 1);
    return Array.from({ length: guideCount }, (_, i) => Math.round(i * step));
  }, [points, guideCount]);

  return (
    <div style={{ position: 'relative', width: CW, userSelect: 'none', paddingTop: 6, paddingBottom: 8, overflow: 'visible' }}>
      <svg
        width={CW}
        height={HEIGHT}
        style={{ display: 'block', overflow: 'visible', cursor: onScrubChange ? 'ew-resize' : 'default' }}
        onPointerDown={handlePointer}
      >
        <defs>
          <linearGradient id="driveChartTempGrad" gradientUnits="userSpaceOnUse" x1={X0} x2={X1} y1={0} y2={0}>
            {points.map((p, idx) => (
              <stop
                key={idx}
                offset={`${(p.dist / (totalDist || 1)) * 100}%`}
                stopColor={tcol(p.displayTemp)}
              />
            ))}
          </linearGradient>

          <linearGradient id="driveChartFade" x1={0} x2={0} y1={0} y2={1}>
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity={dark ? 0.35 : 0.28} />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>

          <mask id="driveChartMask">
            <rect x={0} y={TOP - 10} width={CW} height={AXIS - TOP + 10} fill="url(#driveChartFade)" />
          </mask>
        </defs>

        {/* Vertical guide lines */}
        {guideIndices.map((idx) => {
          const pt = points[idx]!;
          const x = xScale(pt.dist);
          return (
            <line
              key={idx}
              x1={x}
              x2={x}
              y1={TOP - 4}
              y2={AXIS}
              stroke="var(--sep)"
              strokeWidth={0.5}
              strokeDasharray="2 2.5"
            />
          );
        })}

        {/* Rain bars along bottom axis */}
        {points.map((pt, idx) => {
          if (pt.pop < 15) return null;
          const x = xScale(pt.dist);
          const barH = Math.max(5, (pt.pop / 100) * 22);
          return (
            <rect
              key={`rain-${idx}`}
              x={x - 2}
              y={AXIS - barH}
              width={4}
              height={barH}
              rx={2}
              fill="var(--rain)"
              opacity={0.85}
            />
          );
        })}

        {/* Axis hairline */}
        <line x1={0} x2={CW} y1={AXIS} y2={AXIS} stroke="var(--sep)" strokeWidth={0.5} />

        {/* Temperature Area and Line */}
        <path d={areaD} fill="url(#driveChartTempGrad)" mask="url(#driveChartMask)" />
        <path d={pathD} fill="none" stroke="url(#driveChartTempGrad)" strokeWidth={3} strokeLinecap="round" />

        {/* Circles & Temp values at guide points */}
        {guideIndices.map((idx) => {
          const pt = points[idx]!;
          const x = xScale(pt.dist);
          const y = yTemp(pt.displayTemp);
          const roundT = Math.round(pt.displayTemp);

          return (
            <g key={`dot-${idx}`}>
              <circle
                cx={x}
                cy={y}
                r={3.6}
                fill={dark ? '#2C2C2E' : '#FFFFFF'}
                stroke={tcol(pt.displayTemp)}
                strokeWidth={2}
              />
              <text
                x={x}
                y={y - 8}
                textAnchor="middle"
                fontSize={11}
                fontWeight={600}
                fill="var(--label)"
                fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
              >
                {roundT}°
              </text>
            </g>
          );
        })}

        {/* Active scrub pointer line & thumb */}
        <line
          x1={scrubX}
          x2={scrubX}
          y1={6}
          y2={AXIS}
          stroke="var(--accent)"
          strokeWidth={1.5}
        />
        <circle
          cx={scrubX}
          cy={yTemp(
            points.find((p) => p.progress >= scrubProgress)?.displayTemp ??
              points[0]?.displayTemp ??
              20
          )}
          r={6.5}
          fill="#FFFFFF"
          style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))' }}
        />
        <circle
          cx={scrubX}
          cy={yTemp(
            points.find((p) => p.progress >= scrubProgress)?.displayTemp ??
              points[0]?.displayTemp ??
              20
          )}
          r={4.5}
          fill="var(--accent)"
        />

        {/* Bottom labels: ETA and Town/Distance */}
        {guideIndices.map((idx, gIdx) => {
          const pt = points[idx]!;
          const rawX = xScale(pt.dist);
          const anchor = gIdx === 0 ? 'start' : gIdx === guideIndices.length - 1 ? 'end' : 'middle';
          const x = gIdx === 0 ? Math.max(2, rawX) : gIdx === guideIndices.length - 1 ? Math.min(CW - 2, rawX) : rawX;
          const rawName = pt.name || `${Math.round(pt.dist)} ${distUnit}`;
          const timeLabel = isNarrow ? pt.etaClockCompact : pt.etaClock;
          const labelStyle: React.CSSProperties = {
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: '"tnum"',
          };
          const slot = isNarrow ? 64 : 84;
          const boxX = anchor === 'start' ? x : anchor === 'end' ? x - slot : x - slot / 2;

          return (
            <g key={`lbl-${idx}`}>
              <foreignObject x={boxX} y={LABEL_NAME_Y - 12} width={slot} height={28}>
                <div
                  style={{
                    fontSize: labelFontSize,
                    lineHeight: '13px',
                    fontWeight: 600,
                    color: 'var(--label)',
                    textAlign: anchor === 'start' ? 'left' : anchor === 'end' ? 'right' : 'center',
                    overflowWrap: 'anywhere',
                    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                  }}
                >
                  {rawName}
                </div>
              </foreignObject>
              <text
                x={x}
                y={LABEL_TIME_Y}
                textAnchor={anchor}
                fontSize={labelFontSize}
                fontWeight={400}
                fill="var(--label2)"
                fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
                style={labelStyle}
              >
                {timeLabel}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Floating "You · time" Pill */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: Math.max(54, Math.min(CW - 54, scrubX)),
          transform: 'translateX(-50%)',
          height: 22,
          padding: '0 8px',
          borderRadius: 'var(--r-control)',
          background: '#003A8F',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
          pointerEvents: 'none',
        }}
      >
        <CarNavIcon size={13} color="#FFFFFF" style={{ flexShrink: 0 }} />
        <span>You · {scrubEta}</span>
      </div>
    </div>
  );
};
